import * as Sentry from '@sentry/react';
import { ApiError } from '@/lib/api';

export type SentryReportContext = {
  source?: 'query' | 'mutation';
  queryKey?: unknown;
  mutationKey?: unknown;
};

const CLIENT_VALIDATION_PATTERNS = [
  // Form validation thrown from mutationFn (HARBOR-WEB-1B and siblings).
  /^Please (enter|select|tell us|fill in|provide) /i,
  /^No household found/i,
  /^User not ready/i,
  /^No co-parent found/i,
];

export function isClientValidationMessage(message: string): boolean {
  return CLIENT_VALIDATION_PATTERNS.some((pattern) => pattern.test(message.trim()));
}

// Offline / proxy HTML bodies — not app bugs. HARBOR-WEB-11 / HARBOR-WEB-16.
export const TRANSIENT_NETWORK_ERROR_PATTERNS = [
  // Chrome 153+ appends the origin, e.g. Failed to fetch (sfo3.digitaloceanspaces.com)
  // on a journal photo PUT (HARBOR-WEB-H).
  /^Failed to fetch(?:\s+\([^)]+\))?$/i,
  /^Network request failed$/i,
  /^Network request timed out\.?$/i,
  // Safari 16+ appends the host, e.g. Load failed (co-parent.sfo3.cdn.digitaloceanspaces.com)
  // on a failed CDN chunk fetch (HARBOR-WEB-1C).
  /^Load failed(?:\s+\([^)]+\))?$/i,
  /^Network Error$/i,
  /^NetworkError when attempting to fetch resource/i,
  // Safari failed ES-module / dynamic import (HARBOR-WEB-19).
  /^Importing a module script failed\.?$/i,
  // Chrome / Samsung Internet failed dynamic import (HARBOR-WEB-17).
  /^Failed to fetch dynamically imported module/i,
];

export function isTransientClientNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  const message = err.message.trim();
  if (TRANSIENT_NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }
  // apiJson JSON.parse of an HTML error/index page (HARBOR-WEB-16).
  return err instanceof SyntaxError && /Unexpected token ['"]?</.test(message);
}

export function shouldReportError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status >= 400 && err.status < 500) {
      return false;
    }
    if (/unexpected HTML response/i.test(err.message)) {
      return false;
    }
    return true;
  }

  if (isTransientClientNetworkError(err)) {
    return false;
  }

  if (err instanceof Error) {
    const message = err.message.trim();
    if (isClientValidationMessage(message)) {
      return false;
    }
    // Expected missing admin image (Spaces 404) — HARBOR-WEB-13.
    if (/^Preview failed$/i.test(message)) {
      return false;
    }
    // Outlook / Office SafeLinks injection — HARBOR-WEB-J.
    if (/Object Not Found Matching Id:\d+, MethodName:/i.test(message)) {
      return false;
    }
    // Facebook / Instagram Android in-app browser bridge — HARBOR-WEB-1A.
    if (/Java object is gone/i.test(message)) {
      return false;
    }
  }

  if (
    typeof err === 'string' &&
    (/Object Not Found Matching Id:\d+, MethodName:/i.test(err) ||
      /Java object is gone/i.test(err))
  ) {
    return false;
  }

  return true;
}

export function reportError(err: unknown, context?: SentryReportContext): void {
  if (!shouldReportError(err)) {
    return;
  }

  Sentry.captureException(err, {
    extra: context,
    tags: context?.source ? { source: context.source } : undefined,
  });
}
